import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import {
  Badge,
  Button,
  Card,
  ErrorState,
  Field,
  Input,
  Loading,
  PageHeader,
  Select,
  Toggle,
} from '@/components/ui';
import { formatDateTime } from '@/lib/format';

/**
 * Everything the platform emails, and the mail server it sends through.
 *
 * The SMTP settings used to live on the Alerting page, in its third tab. That
 * was wrong twice over: an operator setting up their mailbox had to first find
 * a page about faults, and it is the same one mail server for both, so putting
 * it there made it look like alert-only configuration.
 *
 * Alerting is still where alert RULES live. This is where the mail server, the
 * wording, and the business notifications live.
 */

const IMPLICIT_TLS_PORTS = new Set([465, 8465]);

type Tab = 'events' | 'email' | 'templates' | 'log';

const TABS: { id: Tab; label: string }[] = [
  { id: 'events', label: 'What we send' },
  { id: 'email', label: 'Email settings' },
  { id: 'templates', label: 'Templates' },
  { id: 'log', label: 'Sent' },
];

export default function Notifications() {
  const [tab, setTab] = useState<Tab>('events');

  return (
    <div className="w-full">
      <PageHeader
        title="Notifications"
        description="The ordinary events of running the business, emailed. Alert rules are on the Alerting page."
      />

      <div className="mb-4 flex gap-1 border-b border-line">
        {TABS.map((item) => (
          <button
            key={item.id}
            onClick={() => setTab(item.id)}
            className={
              'px-3 py-2 text-sm font-medium transition-colors ' +
              (tab === item.id
                ? 'border-b-2 border-brand-500 text-brand-700 dark:text-brand-300'
                : 'text-ink-500 hover:text-ink-800 dark:hover:text-ink-200')
            }
          >
            {item.label}
          </button>
        ))}
      </div>

      {tab === 'events' && <Events />}
      {tab === 'email' && <EmailSettings />}
      {tab === 'templates' && <Templates />}
      {tab === 'log' && <SentLog />}
    </div>
  );
}

/* -------------------------------------------------------------------------
 * the mail server
 * ---------------------------------------------------------------------- */
interface ProviderField {
  key: string;
  label: string;
  hint?: string;
}

interface ProviderSpec {
  key: string;
  label: string;
  key_hint: string;
  fields: ProviderField[];
  docs: string;
  blocked_by_hosts: boolean;
}

interface Email {
  provider: string;
  providers: Record<string, Record<string, any>>;
  catalogue: ProviderSpec[];
  smtp_host: string;
  smtp_port: number;
  smtp_username: string;
  smtp_from: string;
  smtp_ssl: boolean;
  smtp_starttls: boolean;
  smtp_default_to: string;
  smtp_password_set: boolean;
  effective_mode: string;
  configured: boolean;
}

function EmailSettings() {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<Record<string, any>>({});
  const [testTo, setTestTo] = useState('');
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);

  const settings = useQuery({
    queryKey: ['notification-email'],
    queryFn: () => api.get<Email>('/admin/notifications/email'),
  });

  const save = useMutation({
    mutationFn: (body: Record<string, any>) => api.put('/admin/notifications/email', body),
    onSuccess: () => {
      setDraft({});
      setResult({ ok: true, text: 'Saved.' });
      qc.invalidateQueries({ queryKey: ['notification-email'] });
    },
    // Without this a rejected save looked exactly like a save that did
    // nothing, which is what "I cannot edit the SMTP settings" was.
    onError: (err: any) =>
      setResult({ ok: false, text: String(err?.message || err) }),
  });

  const test = useMutation({
    mutationFn: () =>
      api.post<{ ok: boolean; error: string | null }>('/admin/notifications/email/test', {
        to: testTo,
      }),
    onSuccess: (data) =>
      setResult({ ok: data.ok, text: data.ok ? 'Test message sent.' : data.error || 'Failed.' }),
    onError: (err: any) => setResult({ ok: false, text: String(err?.message || err) }),
  });

  if (settings.isPending) return <Loading />;
  if (settings.error)
    return <ErrorState error={settings.error} retry={() => settings.refetch()} />;

  const value = { ...(settings.data as any), ...draft };
  const set = (patch: Record<string, any>) => {
    setResult(null);
    setDraft((d) => ({ ...d, ...patch }));
  };

  // 465 is implicit TLS and nothing else. A togglable STARTTLS box beside it
  // invites a combination no compliant server accepts, which is exactly the
  // one that silently times out.
  const portImpliesTls = IMPLICIT_TLS_PORTS.has(Number(value.smtp_port));

  const catalogue = value.catalogue ?? [];
  const chosen: string = draft.provider ?? value.provider ?? 'smtp';
  const spec = catalogue.find((p: ProviderSpec) => p.key === chosen);
  const isSmtp = chosen === 'smtp';
  const stored = (value.providers ?? {})[chosen] ?? {};
  const edits = (draft.provider_config ?? {})[chosen] ?? {};
  const field = (key: string) => edits[key] ?? stored[key] ?? '';
  const setProviderField = (key: string, next: string) =>
    set({
      provider_config: {
        ...(draft.provider_config ?? {}),
        [chosen]: { ...edits, [key]: next },
      },
    });

  return (
    <div className="space-y-4">
      <Card
        title="How mail leaves this platform"
        description="One transport, used by alerts and notifications alike."
      >
        <Field
          label="Send through"
          hint="Most cloud hosts block the SMTP ports on a new account. The providers below send over HTTPS, which nothing blocks."
        >
          <Select
            className="w-72"
            value={chosen}
            onChange={(e) => set({ provider: e.target.value })}
          >
            {catalogue.map((p: ProviderSpec) => (
              <option key={p.key} value={p.key}>
                {p.label}
              </option>
            ))}
          </Select>
        </Field>

        {!isSmtp && spec && (
          <div className="mt-4 space-y-3 border-t border-line pt-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="API key" hint={spec.key_hint}>
                <Input
                  type="password"
                  autoComplete="new-password"
                  placeholder={stored.api_key_set ? '••••••••' : ''}
                  onChange={(e) => setProviderField('api_key', e.target.value)}
                />
              </Field>
              <Field
                label="From address"
                hint="Has to be an address you have verified with them, or they refuse it."
              >
                <Input
                  value={field('from')}
                  placeholder="notifications@example.com"
                  onChange={(e) => setProviderField('from', e.target.value)}
                />
              </Field>
              {spec.fields.map((f: ProviderField) => (
                <Field key={f.key} label={f.label} hint={f.hint}>
                  <Input
                    value={field(f.key)}
                    onChange={(e) => setProviderField(f.key, e.target.value)}
                  />
                </Field>
              ))}
            </div>
            {spec.docs && (
              <p className="text-xs text-ink-500">
                Keys are issued at{' '}
                <a
                  href={spec.docs}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="text-brand-600 hover:underline dark:text-brand-400"
                >
                  {spec.docs}
                </a>
                .
              </p>
            )}
            <p className="text-xs text-ink-500">
              The SMTP settings below are kept exactly as they are. Switch back
              to <strong>SMTP server</strong> whenever the ports are open again
              and nothing needs re-entering.
            </p>
          </div>
        )}
      </Card>

      <Card
        title="SMTP server"
        description={
          isSmtp
            ? 'Everything the platform sends goes through this.'
            : 'Not in use right now. Kept so you can switch back.'
        }
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="SMTP server">
            <Input
              value={value.smtp_host ?? ''}
              placeholder="mail.example.com"
              onChange={(e) => set({ smtp_host: e.target.value })}
            />
          </Field>
          <Field
            label="Port"
            hint="587 for submission, 465 for implicit TLS, 25 for a local relay."
          >
            <Input
              type="number"
              value={value.smtp_port ?? 587}
              onChange={(e) => set({ smtp_port: Number(e.target.value) })}
            />
          </Field>
          <Field label="Username">
            <Input
              value={value.smtp_username ?? ''}
              autoComplete="off"
              onChange={(e) => set({ smtp_username: e.target.value })}
            />
          </Field>
          <Field
            label="Password"
            hint={
              value.smtp_password_set
                ? 'Already set. Leave empty to keep it.'
                : 'Encrypted before it is stored, and never sent back.'
            }
          >
            <Input
              type="password"
              autoComplete="new-password"
              placeholder={value.smtp_password_set ? '••••••••' : ''}
              onChange={(e) => set({ smtp_password: e.target.value })}
            />
          </Field>
          <Field label="From address">
            <Input
              value={value.smtp_from ?? ''}
              placeholder="notifications@example.com"
              onChange={(e) => set({ smtp_from: e.target.value })}
            />
          </Field>
          <Field
            label="Default recipient"
            hint="Used by any alert channel left without one."
          >
            <Input
              value={value.smtp_default_to ?? ''}
              onChange={(e) => set({ smtp_default_to: e.target.value })}
            />
          </Field>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-4">
          <Toggle
            label="STARTTLS"
            checked={portImpliesTls ? false : (value.smtp_starttls ?? true)}
            disabled={portImpliesTls}
            onChange={(v) => set({ smtp_starttls: v })}
          />
          <Toggle
            label="Implicit SSL"
            checked={portImpliesTls || !!value.smtp_ssl}
            disabled={portImpliesTls}
            onChange={(v) => set({ smtp_ssl: v })}
          />
          <Badge tone="gray">{value.effective_mode}</Badge>
        </div>

        {portImpliesTls && (
          <p className="mt-2 text-xs text-ink-500">
            Port {value.smtp_port} is implicit TLS, so this is on regardless.
            Connecting in plain text there means both ends wait for the other to
            speak first, and the send fails twenty seconds later as a timeout.
          </p>
        )}

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-line pt-3">
          <div className="flex items-center gap-2">
            <Input
              value={testTo}
              placeholder="you@example.com"
              className="h-9 w-64 text-sm"
              onChange={(e) => setTestTo(e.target.value)}
            />
            <Button
              variant="secondary"
              disabled={!testTo || test.isPending || !value.smtp_host}
              onClick={() => test.mutate()}
            >
              {test.isPending ? 'Sending…' : 'Send a test'}
            </Button>
          </div>
          <Button
            onClick={() => save.mutate(draft)}
            disabled={save.isPending || !Object.keys(draft).length}
          >
            {save.isPending ? 'Saving…' : 'Save email settings'}
          </Button>
        </div>

        {result && (
          <p
            className={
              'mt-2 text-sm ' +
              (result.ok
                ? 'text-green-600 dark:text-green-400'
                : 'text-red-600 dark:text-red-400')
            }
          >
            {result.text}
          </p>
        )}
      </Card>
    </div>
  );
}

/* -------------------------------------------------------------------------
 * what we send
 * ---------------------------------------------------------------------- */
interface EventRow {
  key: string;
  label: string;
  description: string;
  audiences: string[];
  group: string;
  default_on: boolean;
  enabled: boolean;
  to_admin: boolean;
  to_client: boolean;
  customised: boolean;
}

interface Settings {
  platform_name: string;
  portal_url: string;
  admin_recipients: string[];
  daily_summary_hour: number;
  events: EventRow[];
}

function Events() {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<Record<string, EventRow>>({});
  const [general, setGeneral] = useState({
    platform_name: '',
    portal_url: '',
    admin_recipients: '',
    daily_summary_hour: 7,
  });
  const [testing, setTesting] = useState('');
  const [testTo, setTestTo] = useState('');
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);

  const settings = useQuery({
    queryKey: ['notification-settings'],
    queryFn: () => api.get<Settings>('/admin/notifications'),
  });

  useEffect(() => {
    if (!settings.data) return;
    const byKey: Record<string, EventRow> = {};
    settings.data.events.forEach((e) => {
      byKey[e.key] = e;
    });
    setDraft(byKey);
    setGeneral({
      platform_name: settings.data.platform_name,
      portal_url: settings.data.portal_url,
      admin_recipients: (settings.data.admin_recipients ?? []).join(', '),
      daily_summary_hour: settings.data.daily_summary_hour ?? 7,
    });
  }, [settings.data]);

  const save = useMutation({
    mutationFn: () =>
      api.put('/admin/notifications', {
        platform_name: general.platform_name,
        portal_url: general.portal_url,
        daily_summary_hour: general.daily_summary_hour,
        admin_recipients: general.admin_recipients
          .split(',')
          .map((a) => a.trim())
          .filter(Boolean),
        events: Object.fromEntries(
          Object.values(draft).map((e) => [
            e.key,
            { enabled: e.enabled, audiences: { admin: e.to_admin, client: e.to_client } },
          ]),
        ),
      }),
    onSuccess: () => {
      setResult({ ok: true, text: 'Saved.' });
      qc.invalidateQueries({ queryKey: ['notification-settings'] });
    },
    onError: (err: any) => setResult({ ok: false, text: String(err?.message || err) }),
  });

  const sendTest = useMutation({
    mutationFn: (kind: string) =>
      api.post<{ ok: boolean; error: string | null }>('/admin/notifications/test', {
        kind,
        to: testTo,
      }),
    onSuccess: (data) => {
      setResult({ ok: data.ok, text: data.ok ? 'Sent.' : data.error || 'Failed.' });
      setTesting('');
      qc.invalidateQueries({ queryKey: ['notification-log'] });
    },
    onError: (err: any) => setResult({ ok: false, text: String(err?.message || err) }),
  });

  if (settings.isPending) return <Loading />;
  if (settings.error)
    return <ErrorState error={settings.error} retry={() => settings.refetch()} />;

  const rows = Object.values(draft);
  const groups = [...new Set(rows.map((r) => r.group))];
  const patch = (key: string, next: Partial<EventRow>) => {
    setResult(null);
    setDraft((d) => ({ ...d, [key]: { ...d[key], ...next } }));
  };

  return (
    <div className="space-y-4">
      <Card title="How we introduce ourselves">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Platform name" hint="What customers see us called.">
            <Input
              value={general.platform_name}
              placeholder="Glee AI Bot"
              onChange={(e) => setGeneral({ ...general, platform_name: e.target.value })}
            />
          </Field>
          <Field label="Portal address" hint="Where a client is told to sign in.">
            <Input
              value={general.portal_url}
              placeholder="https://app.example.com"
              onChange={(e) => setGeneral({ ...general, portal_url: e.target.value })}
            />
          </Field>
        </div>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <Field
            label="Internal recipients"
            hint="Comma separated. Left empty, everything internal goes to every active platform user — so each new admin silently starts receiving customer traffic."
          >
            <Input
              value={general.admin_recipients}
              placeholder="ops@example.com, billing@example.com"
              onChange={(e) =>
                setGeneral({ ...general, admin_recipients: e.target.value })
              }
            />
          </Field>
          <Field
            label="Daily summary time"
            hint="In each client's own timezone, so nobody gets theirs at 3am."
          >
            <Select
              value={String(general.daily_summary_hour)}
              onChange={(e) =>
                setGeneral({ ...general, daily_summary_hour: Number(e.target.value) })
              }
            >
              {Array.from({ length: 24 }, (_, h) => (
                <option key={h} value={h}>
                  {String(h).padStart(2, '0')}:00
                </option>
              ))}
            </Select>
          </Field>
        </div>
      </Card>

      {groups.map((group) => (
        <Card title={group} key={group}>
          <div className="space-y-3">
            {rows
              .filter((r) => r.group === group)
              .map((row) => (
                <div key={row.key} className="rounded-md border border-line p-3">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium">{row.label}</span>
                        {!row.default_on && <Badge tone="gray">off by default</Badge>}
                        {row.customised && <Badge tone="blue">custom wording</Badge>}
                      </div>
                      <p className="mt-0.5 text-xs text-ink-500">{row.description}</p>
                    </div>
                    <Toggle
                      label=""
                      checked={row.enabled}
                      onChange={(v) => patch(row.key, { enabled: v })}
                    />
                  </div>

                  {row.enabled && (
                    <div className="mt-2 flex flex-wrap items-center gap-4 border-t border-line pt-2">
                      {row.audiences.includes('admin') && (
                        <Toggle
                          label="To us"
                          checked={row.to_admin}
                          onChange={(v) => patch(row.key, { to_admin: v })}
                        />
                      )}
                      {row.audiences.includes('client') && (
                        <Toggle
                          label="To the client"
                          checked={row.to_client}
                          onChange={(v) => patch(row.key, { to_client: v })}
                        />
                      )}
                      <div className="ml-auto flex items-center gap-2">
                        {testing === row.key ? (
                          <>
                            <Input
                              value={testTo}
                              placeholder="you@example.com"
                              className="h-8 w-56 text-xs"
                              onChange={(e) => setTestTo(e.target.value)}
                            />
                            <Button
                              size="sm"
                              disabled={!testTo || sendTest.isPending}
                              onClick={() => sendTest.mutate(row.key)}
                            >
                              Send
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setTesting('')}>
                              Cancel
                            </Button>
                          </>
                        ) : (
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => {
                              setTesting(row.key);
                              setResult(null);
                            }}
                          >
                            Send me one
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
          </div>
        </Card>
      ))}

      <div className="flex items-center justify-between">
        {result ? (
          <p
            className={
              'text-sm ' +
              (result.ok
                ? 'text-green-600 dark:text-green-400'
                : 'text-red-600 dark:text-red-400')
            }
          >
            {result.text}
          </p>
        ) : (
          <span />
        )}
        <Button onClick={() => save.mutate()} disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------
 * templates
 * ---------------------------------------------------------------------- */
interface Template {
  key: string;
  label: string;
  group: string;
  audiences: string[];
  default_subject: string;
  default_body: string;
  subject: string;
  body: string;
  customised: boolean;
  placeholders: string[];
}

function Templates() {
  const qc = useQueryClient();
  const [open, setOpen] = useState('');
  const [edit, setEdit] = useState<{ subject: string; body: string }>({
    subject: '',
    body: '',
  });
  const [preview, setPreview] = useState<{
    subject: string;
    body: string;
    unknown_placeholders: string[];
  } | null>(null);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);

  const templates = useQuery({
    queryKey: ['notification-templates'],
    queryFn: () =>
      api.get<{ templates: Template[]; sample_note: string }>(
        '/admin/notifications/templates',
      ),
  });

  const save = useMutation({
    mutationFn: (key: string) =>
      api.put(`/admin/notifications/templates/${key}`, edit),
    onSuccess: () => {
      setResult({ ok: true, text: 'Saved.' });
      qc.invalidateQueries({ queryKey: ['notification-templates'] });
      qc.invalidateQueries({ queryKey: ['notification-settings'] });
    },
    onError: (err: any) => setResult({ ok: false, text: String(err?.message || err) }),
  });

  const runPreview = useMutation({
    mutationFn: (key: string) =>
      api.post<{ subject: string; body: string; unknown_placeholders: string[] }>(
        `/admin/notifications/templates/${key}/preview`,
        edit,
      ),
    onSuccess: setPreview,
  });

  if (templates.isPending) return <Loading />;
  if (templates.error)
    return <ErrorState error={templates.error} retry={() => templates.refetch()} />;

  const rows = templates.data?.templates ?? [];

  return (
    <div className="space-y-3">
      <p className="rounded-md bg-sunken p-3 text-sm text-ink-700 dark:text-ink-200">
        {templates.data?.sample_note}
      </p>

      {rows.map((row) => {
        const isOpen = open === row.key;
        return (
          <Card
            key={row.key}
            title={row.label}
            description={row.group}
            actions={
              <div className="flex items-center gap-2">
                {row.customised && <Badge tone="blue">custom</Badge>}
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    if (isOpen) {
                      setOpen('');
                      return;
                    }
                    setOpen(row.key);
                    setEdit({ subject: row.subject, body: row.body });
                    setPreview(null);
                    setResult(null);
                  }}
                >
                  {isOpen ? 'Close' : 'Edit wording'}
                </Button>
              </div>
            }
          >
            {!isOpen ? (
              <div className="text-xs text-ink-500">
                <div className="font-medium text-ink-700 dark:text-ink-200">
                  {row.subject || row.default_subject}
                </div>
                <pre className="mt-1 max-h-24 overflow-hidden whitespace-pre-wrap font-sans">
                  {row.body || row.default_body}
                </pre>
              </div>
            ) : (
              <div className="space-y-3">
                <Field
                  label="Subject"
                  hint="Leave empty to use the built-in wording below."
                >
                  <Input
                    value={edit.subject}
                    placeholder={row.default_subject}
                    onChange={(e) => setEdit({ ...edit, subject: e.target.value })}
                  />
                </Field>
                <Field label="Body" hint="Leave empty to use the built-in wording.">
                  <textarea
                    rows={12}
                    value={edit.body}
                    placeholder={row.default_body}
                    onChange={(e) => setEdit({ ...edit, body: e.target.value })}
                    className="w-full rounded-md border border-line bg-surface p-2 font-mono text-xs text-ink-900 outline-none focus:border-brand-400 dark:text-ink-50"
                  />
                </Field>

                <div className="flex flex-wrap gap-1">
                  {row.placeholders.map((name) => (
                    <button
                      key={name}
                      onClick={() =>
                        setEdit((d) => ({ ...d, body: `${d.body}{${name}}` }))
                      }
                      className="rounded bg-ink-100 px-1.5 py-0.5 font-mono text-2xs text-ink-700 hover:bg-ink-200 dark:bg-ink-800 dark:text-ink-200"
                    >
                      {'{' + name + '}'}
                    </button>
                  ))}
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => runPreview.mutate(row.key)}
                  >
                    Preview
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setEdit({ subject: '', body: '' })}
                  >
                    Reset to built-in
                  </Button>
                  <Button
                    size="sm"
                    className="ml-auto"
                    disabled={save.isPending}
                    onClick={() => save.mutate(row.key)}
                  >
                    {save.isPending ? 'Saving…' : 'Save wording'}
                  </Button>
                </div>

                {result && (
                  <p
                    className={
                      'text-sm ' +
                      (result.ok
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-red-600 dark:text-red-400')
                    }
                  >
                    {result.text}
                  </p>
                )}

                {preview && (
                  <div className="rounded-md border border-line bg-sunken p-3">
                    <div className="text-xs uppercase tracking-wide text-ink-500">
                      Preview
                    </div>
                    <div className="mt-1 text-sm font-medium">{preview.subject}</div>
                    <pre className="mt-2 whitespace-pre-wrap font-sans text-xs text-ink-700 dark:text-ink-200">
                      {preview.body}
                    </pre>
                    {!!preview.unknown_placeholders.length && (
                      <p className="mt-2 text-xs text-amber-700 dark:text-amber-400">
                        Not recognised, and will arrive exactly as typed:{' '}
                        {preview.unknown_placeholders.map((p) => `{${p}}`).join(', ')}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}

/* -------------------------------------------------------------------------
 * what was sent
 * ---------------------------------------------------------------------- */
interface LogRow {
  id: string;
  kind: string;
  label: string;
  client_name: string | null;
  recipients: string[];
  ok: boolean;
  error: string | null;
  created_at: string;
}

function SentLog() {
  const [failuresOnly, setFailuresOnly] = useState(false);

  const log = useQuery({
    queryKey: ['notification-log', failuresOnly],
    queryFn: () =>
      api.get<{ data: LogRow[] }>('/admin/notifications/log', {
        failures_only: failuresOnly,
      }),
    refetchInterval: 60_000,
  });

  if (log.isPending) return <Loading />;

  return (
    <Card
      title="What was actually sent"
      description="Recipients as they resolved at send time, so a contact changed since still shows where it went."
      actions={
        <Toggle label="Failures only" checked={failuresOnly} onChange={setFailuresOnly} />
      }
    >
      {!log.data?.data.length ? (
        <p className="text-sm text-ink-500">
          {failuresOnly ? 'Nothing has failed to send.' : 'Nothing has been sent yet.'}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="glee-table glee-table--compact">
            <thead>
              <tr>
                <th>When</th>
                <th>Notification</th>
                <th>Client</th>
                <th>To</th>
                <th>Result</th>
              </tr>
            </thead>
            <tbody>
              {log.data.data.map((row) => (
                <tr key={row.id}>
                  <td className="tabular-nums text-xs text-ink-500">
                    {formatDateTime(row.created_at)}
                  </td>
                  <td>{row.label}</td>
                  <td className="text-ink-500">{row.client_name ?? '—'}</td>
                  <td className="text-xs text-ink-500">
                    {row.recipients.length ? row.recipients.join(', ') : 'nobody'}
                  </td>
                  <td>
                    {row.ok ? (
                      <Badge tone="green">sent</Badge>
                    ) : (
                      <span className="flex flex-wrap items-center gap-2">
                        <Badge tone="red">failed</Badge>
                        <span className="text-xs text-ink-500">{row.error}</span>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
